import React, { useState, useEffect } from 'react';
import {
  SsContainer,
  ScorecardWrap,
  ScoreRowDiv,
  ScoreBox,
  BoxBg,
  TargetBar,
  BlackBar,
  ScoreTitleDiv,
  ScoreValueDiv,
  TargetDiffSpan,
} from './styles';
import { get, size } from 'global/lib/lodash';
import { getColorFromScore } from 'global/styles';
import { useLoaded } from 'global/hooks/useLoaded';
import { isGob, getOverallScoreDisplay } from '../AssessmentScore/utils';
import { Text } from 'global/components/text';

const TARGET_COLORS = { higher: '#2196F3', lower: '#f44336' };

const ScoreRow = (props) => {
  const { title, score, displayScore, goalScore, scorecardConfig } = props;

  let val = parseFloat(typeof score === 'string' ? score.replace('%', '') : score);
  let goalVal = goalScore != null ? parseFloat(typeof goalScore === 'string' ? goalScore.replace('%', '') : goalScore) : null;

  const hasGoal = goalVal != null && !isNaN(goalVal) && !isNaN(val);
  const diff = hasGoal ? goalVal - val : 0;
  const diffColor = diff >= 0 ? TARGET_COLORS.higher : TARGET_COLORS.lower;

  const formatDiff = (d) => {
    const displayPct = get(scorecardConfig, 'displayPercentage');
    const maxScore = get(scorecardConfig, 'maxScore', 100);
    const minScore = get(scorecardConfig, 'minScore', 0);
    let disp = displayPct ? d : d * ((maxScore - minScore) / 100);
    disp = parseFloat(disp.toFixed(2));
    const prefix = disp >= 0 ? '+' : '';
    return `${prefix}${disp}`;
  };

  return (
    <ScoreRowDiv>
      <ScoreTitleDiv>{title}</ScoreTitleDiv>
      <ScoreBox>
        <BoxBg style={{ width: `${val}%`, backgroundColor: getColorFromScore(val) }} />
        {hasGoal && (
          <BlackBar style={{ left: `calc(2px + ${val}%)` }} />
        )}
        {hasGoal && diff !== 0 && (
          <TargetBar
            style={{
              left: `calc(2px + ${Math.min(val, goalVal)}%)`,
              width: `${Math.abs(diff)}%`,
              backgroundColor: diffColor,
            }}
          />
        )}
      </ScoreBox>
      <ScoreValueDiv>
        {displayScore}
        {hasGoal && (
          <TargetDiffSpan style={{ color: diffColor }}>
            {formatDiff(diff)}
          </TargetDiffSpan>
        )}
      </ScoreValueDiv>
    </ScoreRowDiv>
  );
};

const ScoreCard = (props) => {
  const { scorecard, scores } = props;
  const [overall, setOverall] = useState(null);
  const [detailScores, setDetailScores] = useState([]);

  const scorecardConfig = get(scorecard, 'config');

  const { data, loading } = scores || {};

  const loadProcessed = async () => {
    if (!loading) {
      const all = get(data, scorecard.id, []);
      const overallScore = all.find((d) => d.depth === 0);
      const systemScores = all.filter((d) => d.depth === 1);
      // PFM-2601: When only one system exists, the system row is redundant
      // (its score always equals overall). Show checklist scores instead —
      // mirrors the scoring sidebar's isSingleSystem flat list.
      const isSingleSystem = size(systemScores) === 1;
      const rows = isSingleSystem
        ? all.filter((d) => d.depth === 2 && !isGob(d) && d.value !== null)
        : systemScores.filter((d) => d.value !== null);
      setOverall(overallScore);
      setDetailScores(rows);
    }
  };

  useEffect(() => {
    loadProcessed();
  }, [data, loading]);

  return (
    <ScorecardWrap>
      <Text type="title_medium" center sx={{ display: 'block', mb: 1 }}>
        {get(scorecard, 'name')}
      </Text>

      <ScoreRow
        title="Overall"
        score={get(overall, 'value')}
        displayScore={getOverallScoreDisplay(overall, 'displayScore')}
        goalScore={get(overall, 'goal.value')}
        scorecardConfig={scorecardConfig}
      />

      {detailScores.map((sc, i) => (
        <ScoreRow
          key={i}
          title={sc.name}
          displayScore={sc.displayScore || sc.value}
          score={sc.value || sc.displayScore}
          goalScore={get(sc, 'goal.value')}
          scorecardConfig={scorecardConfig}
        />
      ))}
    </ScorecardWrap>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const { assessment, canShowScores, scores } = props;

  const cards = get(assessment, 'data.scorecards', []);

  useLoaded(props, [!cards]);

  return !canShowScores ? null : (
    <SsContainer>
      <Text type="title_large" center sx={{ display: 'block', mb: 2 }}>
        Score Summary
      </Text>

      {cards.map((card, i) => (
        <ScoreCard key={i} scorecard={card} scores={scores} scorecardIndex={i} assessment={assessment} />
      ))}
    </SsContainer>
  );
};
